/**
 * Unified employee avatar utilities
 *
 * Single source of truth for avatar colors and initials across the app.
 */

/** Canonical 10-color palette for employee avatars */
export const EMPLOYEE_AVATAR_COLORS = [
  'bg-blue-500',
  'bg-green-500',
  'bg-purple-500',
  'bg-orange-500',
  'bg-pink-500',
  'bg-cyan-500',
  'bg-indigo-500',
  'bg-teal-500',
  'bg-rose-500',
  'bg-amber-500',
] as const;

export type EmployeeAvatarColor = (typeof EMPLOYEE_AVATAR_COLORS)[number];

/**
 * Deterministic color for an employee based on their name.
 * Uses djb2-style hash for consistent results.
 *
 * Accepts null/undefined so callers don't have to defensively coerce —
 * employee name can legitimately be missing for partially-loaded rows.
 */
export function getEmployeeAvatarColor(name: string | null | undefined): EmployeeAvatarColor {
  const safe = name ?? '';
  let hash = 0;
  for (let i = 0; i < safe.length; i++) {
    hash = safe.charCodeAt(i) + ((hash << 5) - hash);
  }
  return EMPLOYEE_AVATAR_COLORS[Math.abs(hash) % EMPLOYEE_AVATAR_COLORS.length];
}

/**
 * Get 2-character initials from a name.
 * - Multi-word: first[0] + last[0]
 * - Single word: slice(0, 2)
 * - Empty / null / undefined: '?'
 */
export function getEmployeeInitials(name: string | null | undefined): string {
  const trimmed = (name ?? '').trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return trimmed.slice(0, 2).toUpperCase();
}
